import { ShoppingCartProductCategory } from '../../../../models/shopping-cart/shopping-cart-product-category';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CarpentryConfiguratorItem } from '../../configurator/applications/carpentry-configurator-item';
import { CarpentryService } from '../../../../services/configurators-2d/applications/carpentry.service';
import { ModalService } from 'src/app/services/modal.service';
import { QuotationRecord } from 'src/app/models/configurators-2d/applications/carpentry/quotation-record';
import { ISummaryComponent } from '../../configurator/i-summary-component';
import { ShoppingCartProductList } from 'src/app/models/shopping-cart/shopping-cart-product-list';
import { ShoppingCartProduct } from 'src/app/models/shopping-cart/shopping-cart-product';
import { Order } from 'src/app/models/configurators-2d/order';
import { Discount } from 'src/app/models/configurators-2d/applications/carpentry/discount/discount';
import { ShoppingCartDiscount } from 'src/app/models/shopping-cart/shopping-cart-discount';
import { ShoppingCartAssistance } from 'src/app/models/shopping-cart/shopping-cart-assistance';

@Component({
  selector: 'ng-carpentry-summary',
  templateUrl: './summary.component.html',
})
export class CarpentrySummaryComponent
  extends CarpentryConfiguratorItem
  implements OnInit, OnChanges, ISummaryComponent
{
  /** Rechargement du component */
  @Input() refresh: boolean;

  /** Services sélectionnés */
  @Input() selectedAssistances: ShoppingCartAssistance[];

  /** Revenir à une page précédente */
  @Output() previous: EventEmitter<any> = new EventEmitter<number>();

  /** Fin de rechargement du component */
  @Output() refreshFinished: EventEmitter<any> = new EventEmitter<boolean>();

  /** En cours de chargement */
  loader: boolean = true;

  /** Liste des produits */
  productsList: ShoppingCartProductList = new ShoppingCartProductList([]);

  /** Liste des remises */
  discounts: ShoppingCartDiscount[];

  constructor(
    public carpentryService: CarpentryService,
    public modalService: ModalService
  ) {
    super(carpentryService);
  }

  ngOnInit(): void {
    this.loader = true;
    this.service.getProductDiscounts().then(discounts => {
      this.discounts = discounts;
      this.getProductList().then(() => (this.loader = false));
    });
  }

  /** Réinitialiser le component lors du changement d'url projet */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.refresh && changes.refresh.currentValue === true) {
      this.ngOnInit();
    }
  }

  /** Fin du rechargement du component */
  refreshFinishedSet(): void {
    this.refreshFinished.emit();
  }

  /** Mise à jour du LastUserModification */
  updateConfigurationHasChanged(event: any): void {
    this.configuration.configurationHasChanged = event;
  }

  /** Fonction de création de la liste des produits */
  async getProductList(): Promise<void> {
    const shoppingCartCategoriesMap = new Map<string, ShoppingCartProductCategory>();
    
    for (const element of this.configuration.quotationRecords) {
      let q = element;
      /** Association de la remise (même 0%) pour récupérer l'EAN */
      q = await this.updateDiscount(q, q.discount == undefined);

      //création du productList
      const product: ShoppingCartProduct = this.service.convertToShoppingCartProduct(q);

      const key = q.supplierName ?? '';
      let shoppingCartCategory = shoppingCartCategoriesMap.get(key);
      if (!shoppingCartCategory) {
        shoppingCartCategory = new ShoppingCartProductCategory(key, undefined, []);
        shoppingCartCategoriesMap.set(key, shoppingCartCategory);
      }
      shoppingCartCategory.shoppingCartProducts.push(product);
    }

    this.productsList.shoppingCartProductCategories = Array.from(shoppingCartCategoriesMap.values())
      .toSorted((a, b) => a.name.localeCompare(b.name));
    for (const cat of this.productsList.shoppingCartProductCategories) {
      cat.shoppingCartProducts = cat.shoppingCartProducts.toSorted((a, b) => a.index - b.index);
    }

    if (this.configuration.assistances != undefined) {
      if (this.configuration.assistances.length > 0) {
        this.productsList.shoppingCartAssistances = [];
        this.configuration.assistances.forEach(s =>
          this.productsList.shoppingCartAssistances.push(this.service.convertToShoppingCartService(s))
        );
        this.configuration.assistances.sort((a, b) => a.assistanceType.label.localeCompare(b.assistanceType.label));
      } else {
        this.configuration.assistances = undefined;
      }
    }

    this.loader = false;
  }

  /**
   * Complète l'ean en fonction de la remise appliquée
   * @param product
   * @param defaultDiscount si aucune remise appliquée, appliquer la remise par defaut
   * @returns ShoppingCartProduct
   */
  async updateDiscount(product: QuotationRecord, defaultDiscount: boolean): Promise<QuotationRecord> {
    let currentDiscount = new Discount();
    if (this.discounts) {
      let discountProduct = this.discounts.filter(d => d.productCode === product.productCode);
      if (discountProduct) {
        if (defaultDiscount) {
          //cas appliquer remise par defaut
          if (discountProduct.find(d => d.isDefault) != undefined) {
            currentDiscount = discountProduct.find(d => d.isDefault);
          } else {
            //si pas de remise par défaut, prendre la remise avec la plus petite valeur
            currentDiscount = discountProduct.sort((a, b) => a.discountValue - b.discountValue)[0];
          }
        } else {
          //cas remise existante (même 0%)
          currentDiscount = discountProduct.find(d => d.discountValue == product.discount);
          if (currentDiscount == undefined) {
            //cas remise expirée
            let order = this.getLastOrder();
            if (order != undefined) {
              //cas devis existant
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              if (new Date(order.quotationExpirationDate) >= today) {
                //si devis non expiré, on ajoute la remise expirée à la liste des remises
                currentDiscount = await this.carpentryService.getProductDiscount(product.productCode, product.discount);
                this.discounts.push(currentDiscount);
              }
            }

            if (currentDiscount == undefined) {
              product.discount = 0;
              currentDiscount = discountProduct.find(d => d.discountValue == product.discount);
            }
          }
        }
      }
    }

    if (currentDiscount != undefined) {
      product.discount = currentDiscount.discountValue;
      product.ean = currentDiscount.ean;
    }
    return product;
  }

  /** Revenir à une page précédente */
  previousStep(eventData: { externalId: number; numberPagePrevious: number }): void {
    this.previous.emit({ externalId: eventData.externalId, numberPagePrevious: eventData.numberPagePrevious });
  }

  /**
   * Renvoie la commande la plus récente
   * @returns Order
   */
  getLastOrder(): Order {
    if (!this.configuration.orders || this.configuration.orders.length == 0) {
      return undefined;
    }

    // Renvoyer l'order ayant la sendDate la plus récente
    return this.configuration.orders.reduce((prevOrder, currentOrder) => {
      if (currentOrder.sendDate > prevOrder.sendDate) {
        return currentOrder;
      } else {
        return prevOrder;
      }
    });
  }

  /**
   * Met à jour la configuration avec la liste de produits
   * @param productList ShoppingCartProductList
   */
  productListUpdate(productList: ShoppingCartProductList): void {
    this.carpentryService.addProductListToConfiguration(productList);
    this.configuration.assistances = productList.shoppingCartAssistances;
  }

  isNextButtonActive(): boolean {
    return true;
  }

  checkPageChangeCapability(): boolean {
    return true;
  }

  isOrderButtonDisabled(): boolean {
    return this.configuration.quotationRecords.some(q => q.supplierSent);
  }
}
